/**
 * ProductTour — the homepage walkthrough of the signed-in product.
 * @file src/web/features/main/components/ProductTour.tsx
 *
 * ── Why this is not a grid of four screenshots ────────────────────────────
 *
 * It was. Four thumbnails side by side, each too small to read, which asks a
 * reader to squint at everything at once and gives them no order to read it
 * in. A product is a sequence — here is what you see first, here is where you
 * go next — and a walkthrough can show one screen large enough to actually
 * read while naming the step it belongs to.
 *
 * ── The rules motion has to follow ────────────────────────────────────────
 *
 * An image that changes while somebody is reading it is hostile, so:
 *
 *   - it pauses on hover and on keyboard focus anywhere inside;
 *   - it stops for good the moment somebody picks a step themselves, because
 *     a carousel that keeps moving after you have taken the wheel is fighting
 *     you;
 *   - it never starts under prefers-reduced-motion, which is a statement
 *     about moving pictures and not only about animation;
 *   - every panel is in the DOM with its alt text, hidden with `hidden`
 *     rather than unmounted, so a crawler and a screen reader get all four
 *     whatever the carousel is doing.
 *
 * The progress bar is keyed on the active index so it restarts cleanly rather
 * than animating from wherever the last one stopped.
 */
import * as React from 'react';
import { Gauge, Globe, CreditCard, Rocket } from 'lucide-react';

interface Step {
  key: string;
  /** The tab label — a place in the product, not a feature name. */
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** One line under the image, saying what the reader is looking at. */
  caption: string;
  alt: string;
}

const STEPS: Step[] = [
  {
    key: 'dashboard',
    label: 'Everything at a glance',
    icon: Gauge,
    caption:
      'Open it and the first line tells you whether anything needs you. Applications, domains and certificates, then what has shipped recently — the questions you would otherwise have to ask us.',
    alt: 'The Bloomneo dashboard: an all-systems-normal banner, tiles for applications, domains and storage, and a chart of recent releases',
  },
  {
    key: 'apps',
    label: 'Your applications',
    icon: Rocket,
    caption:
      'Production and testing kept apart, each with the commit it is running and when it last shipped. Open one to see its addresses and release history.',
    alt: 'The App deploy page listing production and testing applications with their latest release and commit',
  },
  {
    key: 'domains',
    label: 'Domains and SSL',
    icon: Globe,
    caption:
      'Every address we answer for you and the state of its certificate. Renewals happen before expiry without anyone remembering — this is where you check that they did.',
    alt: 'The Domains and SSL page showing DNS zones, mapped domains and certificate status reading all secure',
  },
  {
    key: 'billing',
    label: 'Your plan',
    icon: CreditCard,
    caption:
      'What your package covers and what you are using against it, with the other packages beside it. Moving up, moving down and cancelling all start here.',
    alt: 'The Billing page showing the current package, usage bars for applications and domains, and the other packages available',
  },
];

/** Long enough to read the caption, short enough that nobody waits. */
const ADVANCE_MS = 7000;

export function ProductTour() {
  const [active, setActive] = React.useState(0);
  /** Set once somebody chooses a step; the carousel never resumes after. */
  const [taken, setTaken] = React.useState(false);
  const [paused, setPaused] = React.useState(false);

  const reduced = React.useRef(false);
  React.useEffect(() => {
    reduced.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (reduced.current) setTaken(true);
  }, []);

  React.useEffect(() => {
    if (taken || paused) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % STEPS.length), ADVANCE_MS);
    return () => window.clearTimeout(id);
  }, [active, taken, paused]);

  function choose(i: number) {
    setActive(i);
    setTaken(true);
  }

  const running = !taken && !paused;

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* ── The steps ───────────────────────────────────────────────── */}
      <div role="tablist" aria-label="Product walkthrough" className="grid gap-2 sm:grid-cols-4">
        {STEPS.map((step, i) => {
          const on = i === active;
          return (
            <button
              key={step.key}
              type="button"
              role="tab"
              aria-selected={on}
              aria-controls={`tour-${step.key}`}
              id={`tour-tab-${step.key}`}
              onClick={() => choose(i)}
              className={`group cursor-pointer rounded-lg border px-3.5 py-3 text-left transition-colors ${
                on ? 'border-primary/50 bg-primary/5' : 'border-border hover:bg-muted/60'
              }`}
            >
              <span className="flex items-center gap-2">
                <step.icon className={`size-4 shrink-0 ${on ? 'text-primary' : 'text-muted-foreground'}`} />
                <span className={`text-sm ${on ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </span>
              </span>
              {/*
                A rail under every step so the row does not jump by a pixel
                when the active one gains its bar. The fill is keyed on the
                active index, which remounts it and restarts the animation
                rather than resuming from where the previous one left off.
              */}
              <span className="mt-2 block h-0.5 w-full overflow-hidden rounded-full bg-border">
                {on && (
                  <span
                    key={`${active}-${running}`}
                    className="block h-full rounded-full bg-primary"
                    style={
                      running
                        ? { animation: `tour-progress ${ADVANCE_MS}ms linear forwards` }
                        : { width: '100%' }
                    }
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── The screen ──────────────────────────────────────────────── */}
      <div className="mt-5 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        {STEPS.map((step, i) => (
          <div
            key={step.key}
            role="tabpanel"
            id={`tour-${step.key}`}
            aria-labelledby={`tour-tab-${step.key}`}
            hidden={i !== active}
          >
            {/*
              Both themes ship and CSS picks one, keyed on `html.dark` — the
              class ThemeProvider sets. Tailwind's own `dark:` variant is
              configured for media here, so it would follow the operating
              system and contradict the site's own toggle.
            */}
            <img
              src={`/product/${step.key}-light.png`}
              alt={step.alt}
              width={1280}
              height={760}
              loading={i === 0 ? 'eager' : 'lazy'}
              className="block w-full [.dark_&]:hidden"
            />
            <img
              src={`/product/${step.key}-dark.png`}
              alt={step.alt}
              width={1280}
              height={760}
              loading={i === 0 ? 'eager' : 'lazy'}
              className="hidden w-full [.dark_&]:block"
            />
          </div>
        ))}
      </div>

      {/*
        Fixed height so the caption does not shuffle the page as the steps
        change — the longest of the four sets it, and a shorter one leaves
        space rather than moving everything below it up.
      */}
      <p className="mt-3 min-h-[3.5rem] max-w-2xl text-sm leading-relaxed text-muted-foreground sm:min-h-[3rem]">
        {STEPS[active].caption}
      </p>
    </div>
  );
}
